import React, { useEffect, useState } from "react";
import { GoogleMap, InfoWindow, Marker } from "@react-google-maps/api";
import moment from "moment";
import { IDeliveryBoardItem, IDeliveryDriver } from "../../common/interfaces";
import { useShippingCalculator } from "../../hooks/useShippingCalculator";

// Repartidor: moto de Material Icons (Apache 2.0), igual que en el seguimiento del cliente.
const MOTO_ICON =
  "data:image/svg+xml;charset=UTF-8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 52 52">' +
      '<circle cx="26" cy="26" r="22" fill="#111" stroke="#F5E03B" stroke-width="3"/>' +
      '<g transform="translate(12 12) scale(1.17)"><path fill="#F5E03B" d="M18.98 6.98Q18.98 6.19 18.4 5.6Q17.81 5.02 17.02 5.02H14.02V6.98H17.02V9.66L13.5 14.02H9.98V9H6Q4.88 9 3.98 9.54Q3.09 10.08 2.55 10.99Q2.02 11.91 2.02 12.98V15.98H3.98Q3.98 16.83 4.41 17.51Q4.83 18.19 5.51 18.59Q6.19 18.98 6.98 18.98Q7.83 18.98 8.51 18.59Q9.19 18.19 9.59 17.51Q9.98 16.83 9.98 15.98H14.48L18.98 10.36ZM6.98 17.02Q6.61 17.02 6.3 16.71Q6 16.41 6 15.98H8.02Q8.02 16.41 7.71 16.71Q7.41 17.02 6.98 17.02ZM5.02 6H9.98V8.02H5.02ZM18.98 12.98Q18.19 12.98 17.51 13.41Q16.83 13.83 16.41 14.51Q15.98 15.19 15.98 15.98Q15.98 16.83 16.41 17.51Q16.83 18.19 17.51 18.59Q18.19 18.98 18.98 18.98Q19.83 18.98 20.51 18.59Q21.19 18.19 21.59 17.51Q21.98 16.83 21.98 15.98Q21.98 15.19 21.59 14.51Q21.19 13.83 20.51 13.41Q19.83 12.98 18.98 12.98ZM18.98 17.02Q18.61 17.02 18.3 16.71Q18 16.41 18 15.98Q18 15.61 18.3 15.3Q18.61 15 18.98 15Q19.41 15 19.71 15.3Q20.02 15.61 20.02 15.98Q20.02 16.41 19.71 16.71Q19.41 17.02 18.98 17.02Z"/></g></svg>'
  );

interface Props {
  deliveries: IDeliveryBoardItem[];
  drivers: IDeliveryDriver[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}

const containerStyle = { width: "100%", height: "100%", borderRadius: 8 };
const REYNOSA = { lat: 26.0700901, lng: -98.3168376 };

// Mapa del tablero: repartidores en turno (amarillo) y domicilios de los pedidos abiertos.
export const DeliveryMap: React.FC<Props> = ({ deliveries, drivers, selectedId, onSelect }) => {
  // Mismo cargador del script que el cotizador de envío (no duplica <script>).
  const { isLoaded } = useShippingCalculator();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [openDriver, setOpenDriver] = useState<number | null>(null);
  const [fitted, setFitted] = useState(false);

  const located = deliveries.filter((d) => d.address.lat !== null && d.address.lng !== null);
  const onMap = drivers.filter((d) => d.on_shift && d.lat !== null && d.lng !== null);

  useEffect(() => {
    if (!map || fitted) return;
    const bounds = new google.maps.LatLngBounds();
    located.forEach((d) => bounds.extend({ lat: d.address.lat!, lng: d.address.lng! }));
    onMap.forEach((d) => bounds.extend({ lat: d.lat!, lng: d.lng! }));
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, 60);
      // Con un solo punto (p. ej. sólo el repartidor) fitBounds se acerca hasta la calle.
      google.maps.event.addListenerOnce(map, "idle", () => {
        if ((map.getZoom() ?? 0) > 15) map.setZoom(15);
      });
      setFitted(true);
    }
  }, [map, located.length, onMap.length, fitted]);

  useEffect(() => {
    const d = deliveries.find((x) => x.id === selectedId);
    if (map && d && d.address.lat !== null) map.panTo({ lat: d.address.lat, lng: d.address.lng! });
  }, [selectedId]);

  if (!isLoaded) return <div style={{ color: "#aaa", padding: 16 }}>Cargando mapa…</div>;

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={REYNOSA}
      zoom={12}
      onLoad={setMap}
      onUnmount={() => setMap(null)}
      options={{
        styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }],
        fullscreenControl: false,
        mapTypeControl: false,
        streetViewControl: false,
      }}
    >
      {located.map((d) => (
        <Marker
          key={`d${d.id}`}
          position={{ lat: d.address.lat!, lng: d.address.lng! }}
          label={{ text: String(d.venta_id).slice(-3), color: "#fff", fontSize: "10px", fontWeight: "700" }}
          onClick={() => onSelect(d.id)}
          icon={{
            path: google.maps.SymbolPath.CIRCLE,
            scale: d.id === selectedId ? 14 : 11,
            fillColor: d.status === "failed" ? "#d32f2f" : d.status === "in_route" ? "#1e3a8a" : "#111",
            fillOpacity: 1,
            strokeColor: "#fff",
            strokeWeight: 2,
          }}
        />
      ))}
      {onMap.map((d) => (
        <Marker
          key={`r${d.id}`}
          position={{ lat: d.lat!, lng: d.lng! }}
          zIndex={1000}
          onClick={() => setOpenDriver(d.id)}
          icon={{
            url: MOTO_ICON,
            scaledSize: new google.maps.Size(40, 40),
            anchor: new google.maps.Point(20, 20),
          }}
        >
          {openDriver === d.id && (
            <InfoWindow onCloseClick={() => setOpenDriver(null)}>
              <div style={{ color: "#111" }}>
                <b>{d.name}</b>
                <br />
                {d.in_route} en ruta · {d.assigned} asignados
                <br />
                {d.location_at ? `ubicación ${moment(d.location_at).fromNow()}` : ""}
              </div>
            </InfoWindow>
          )}
        </Marker>
      ))}
    </GoogleMap>
  );
};
